import { Link } from "react-router-dom";
import PersonLeaderboard from "../components/PersonLeaderboard";
import HeadToHeadCard from "../components/HeadToHeadCard";
import type { AppDataState } from "../api/useAppData";
import type { AppData } from "../../shared/types";
import { isKickedOffAwaitingData } from "../lib/fixtures";

export default function HomePage({ state }: { state: AppDataState }) {
  const { data, refreshing } = state;
  if (!data) {
    return <SkeletonHome />;
  }

  const kickedOff = data.allFixtures.filter((f) => isKickedOffAwaitingData(f));
  const liveFixtures = [...data.liveFixtures, ...kickedOff];
  const upcoming = data.upcomingHeadToHeads.filter(
    (f) => !isKickedOffAwaitingData(f)
  );
  const recent = [...data.recentResults]
    .sort(
      (a, b) => new Date(b.dateUtc).getTime() - new Date(a.dateUtc).getTime()
    )
    .slice(0, 5);

  const isPreTournament =
    data.allFixtures.length > 0 &&
    Date.now() <
      Math.min(...data.allFixtures.map((f) => new Date(f.dateUtc).getTime()));
  const finalFixture = data.knockoutFixtures["Final"]?.[0];
  const isPostTournament =
    finalFixture?.status === "finished" && !finalFixture.isPlaceholder;

  return (
    <div className="home-grid">
      <div className="home-main">
        <section className="title-block">
          <h1>Biffles World Cup Sweepstake</h1>
          <div className="last-updated">
            {data.lastUpdated ? (
              <>
                Last updated {new Date(data.lastUpdated).toLocaleTimeString()}
                {refreshing && (
                  <>
                    {" "}
                    <span className="refresh-pill">Refreshing…</span>
                  </>
                )}
              </>
            ) : (
              "Loading…"
            )}
          </div>
        </section>

        <section className="section card">
          <h2>
            Leaderboard {isPostTournament ? "· Final standings" : null}
            {isPreTournament ? (
              <span className="refresh-pill">
                {preTournamentSubtitle(data)}
              </span>
            ) : null}
          </h2>
          <PersonLeaderboard rows={data.leaderboard} compact />
        </section>

        {liveFixtures.length > 0 ? (
          <section className="section" aria-live="polite">
            <h2>Live</h2>
            <div className="h2h-grid">
              {liveFixtures.map((f) => (
                <HeadToHeadCard key={f.id} fixture={f} />
              ))}
            </div>
          </section>
        ) : null}

        {!isPostTournament && upcoming.length > 0 ? (
          <section className="section">
            <h2>Upcoming head-to-heads</h2>
            <div className="h2h-grid">
              {upcoming.slice(0, 6).map((f) => (
                <HeadToHeadCard key={f.id} fixture={f} />
              ))}
            </div>
            <div style={{ marginTop: 6 }}>
              <Link to="/head-to-heads">View all →</Link>
            </div>
          </section>
        ) : null}

        {recent.length > 0 ? (
          <section className="section">
            <h2>Recent results</h2>
            <div className="h2h-grid">
              {recent.map((f) => (
                <HeadToHeadCard key={f.id} fixture={f} />
              ))}
            </div>
          </section>
        ) : null}

        <MiniStats data={data} />
      </div>
    </div>
  );
}

function preTournamentSubtitle(data: AppData): string {
  const earliest = Math.min(
    ...data.allFixtures.map((f) => new Date(f.dateUtc).getTime())
  );
  const days = Math.max(
    0,
    Math.round((earliest - Date.now()) / (24 * 3600 * 1000))
  );
  if (days === 0) return "Tournament starts today";
  if (days === 1) return "Tournament starts tomorrow";
  return `Tournament starts in ${days} days`;
}

function formatSigned(n: number): string {
  if (n > 0) return `+${n}`;
  return String(n);
}

function MiniStats({ data }: { data: AppData }) {
  const finishedCount = data.allFixtures.filter(
    (f) => f.status === "finished" && !f.isPlaceholder
  ).length;

  if (finishedCount < 2) {
    return (
      <section className="section card">
        <h2>Mini stats</h2>
        <div className="empty-card">Tournament not started yet</div>
      </section>
    );
  }

  const e = data.extraStats;
  return (
    <section className="section card">
      <h2>Mini stats</h2>
      <ul className="mini-stats-list">
        {e.bestTeamOwned ? (
          <li>
            <span className="label">Best team:</span>
            {e.bestTeamOwned.team} ({e.bestTeamOwned.owner}){" "}
            {formatSigned(e.bestTeamOwned.goalDifference)} GD
          </li>
        ) : null}
        {e.worstTeamOwned ? (
          <li>
            <span className="label">Worst team:</span>
            {e.worstTeamOwned.team} ({e.worstTeamOwned.owner}){" "}
            {formatSigned(e.worstTeamOwned.goalDifference)} GD
          </li>
        ) : null}
        {e.mostBrutalDefeat ? (
          <li>
            <span className="label">Biggest win:</span>
            {e.mostBrutalDefeat.homeTeam} {e.mostBrutalDefeat.homeGoals}–
            {e.mostBrutalDefeat.awayGoals} {e.mostBrutalDefeat.awayTeam} (
            {e.mostBrutalDefeat.homeOwner} v {e.mostBrutalDefeat.awayOwner})
          </li>
        ) : null}
        {e.mostGoalsInOneMatch ? (
          <li>
            <span className="label">Most goals:</span>
            {e.mostGoalsInOneMatch.homeTeam} {e.mostGoalsInOneMatch.homeGoals}–
            {e.mostGoalsInOneMatch.awayGoals}{" "}
            {e.mostGoalsInOneMatch.awayTeam}
          </li>
        ) : null}
      </ul>
    </section>
  );
}

function SkeletonHome() {
  return (
    <>
      <div className="skeleton-card">
        <div className="skeleton-block" style={{ width: "60%", height: 22 }} />
        <div className="skeleton-block" style={{ width: "40%" }} />
      </div>
      <div className="skeleton-card section">
        {Array.from({ length: 6 }).map((_, i) => (
          <div className="skeleton-block" key={i} />
        ))}
      </div>
    </>
  );
}
